import { isPremiumRequiredError } from '@/utils/httpAuth'
import { currentTrialDays } from '@/composables/usePricing'

/**
 * Utility to handle API errors and return localized error messages.
 * 
 * @param {Error} error The error object from axios
 * @param {Function} t The translation function ($t or t from useI18n)
 * @param {String} defaultKey The default translation key if no specific error is found
 * @returns {String} Localized error message
 */
export function getApiErrorMessage(error, t, defaultKey = 'auth.unknown_error') {
  // Network error (no response received)
  if (!error.response) {
    return t('auth.network_error');
  }

  const status = error.response.status;
  const data = error.response.data;

  if (isPremiumRequiredError(error)) {
    if (data.message) return data.message;
    const days = currentTrialDays();
    return days > 0
      ? t('auth.premium_required', { days, n: days })
      : t('auth.premium_required_no_trial');
  }

  // Authentication errors (401 Unauthorized, 403 Forbidden — session / access)
  if (status === 401 || status === 403) {
    return data.message || t('auth.login_error');
  }

  // Validation errors (422 Unprocessable Entity)
  if (status === 422) {
    if (data.errors) {
      // Return the first validation error message if available
      const firstErrorKey = Object.keys(data.errors)[0];
      if (data.errors[firstErrorKey] && data.errors[firstErrorKey].length > 0) {
        return data.errors[firstErrorKey][0];
      }
    }
    return data.message || t(defaultKey);
  }

  // Rate limiting (429 Too Many Requests). Laravel answers with a bare English
  // "Too Many Attempts.", which is what a throttled visitor was shown: no
  // translation, and nothing about what to do next. Retry-After is in seconds.
  if (status === 429) {
    const retryAfter = Number(error.response.headers?.['retry-after']);
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      return t('auth.rate_limited_retry', { minutes: Math.max(1, Math.ceil(retryAfter / 60)) });
    }
    return t('auth.rate_limited');
  }

  // Server errors (500+)
  if (status >= 500) {
    return t('auth.server_error');
  }

  // Fallback to response message or default
  return data.message || t(defaultKey);
}
